import { Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, Container, IconButton, Snackbar, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MainHeader from '../components/MainHeader';
import DrawerHeader from '../components/DrawerHeader';
import Main from '../components/Main';
import CopyImageButton from '../components/CopyImageButton';
import CopyExcelDataButton from '../components/CopyExcelDataButton';

function PremiumIncreaseCalculator() {
  const [open, setOpen] = useState(localStorage.getItem('quoting-drawer-open') == 'true');
  const [showImageCopy, setShowImageCopy] = useState(false);

  const [oldYear, setOldYear] = useState('');
  const [newYear, setNewYear] = useState('');

  const [clients, setClients] = useState([
    { id: 1, name: '', typeOfCover: '', oldPremium: '', newPremium: '' }
  ]);

  function addClient() {
    const newId = clients.length > 0 ? Math.max(...clients.map(c => c.id)) + 1 : 1;
    setClients([...clients, { id: newId, name: '', typeOfCover: '', oldPremium: '', newPremium: '' }]);
  }

  function removeClient(id) {
    if (clients.length <= 1) return;
    setClients(clients.filter(c => c.id !== id));
  }

  function updateClient(id, field, value) {
    setClients(clients.map(c => c.id === id ? { ...c, [field]: value } : c));
  }

  function calculateIncrease(oldPremium, newPremium) {
    const oldVal = parseFloat(oldPremium);
    const newVal = parseFloat(newPremium);
    if (isNaN(oldVal) || isNaN(newVal) || oldVal === 0) return '';
    const increase = ((newVal - oldVal) / oldVal) * 100;
    return increase.toFixed(2) + '%';
  }

  function formatNumber(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return (
    <Fragment>
      <Box sx={{ display: 'flex' }}>
        <MainHeader open={open} setOpen={setOpen} />
        <Main open={open} sx={{ pt: 4 }}>
          <DrawerHeader />

          <Container maxWidth="lg">
            <Typography variant="h2" align="center" fontWeight={600} fontSize={32} sx={{ mb: 1, mt: 0 }}>
              Annual Premium Increase
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={addClient} size="small">
                Add Client
              </Button>
            </Box>

            {/* Input Table */}
            <Box sx={{ mb: 3 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#050A30', color: '#ebe6e6' }}>
                    <td style={{ padding: '10px 15px', width: '40px' }}></td>
                    <td style={{ padding: '10px 15px' }}>Client Data</td>
                    <td style={{ padding: '10px 15px' }}>Type of Cover</td>
                    <td style={{ padding: '10px 15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Old Premium</span>
                        <TextField
                          size="small"
                          placeholder="Year"
                          value={oldYear}
                          onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setOldYear(e.target.value); }}
                          variant="filled"
                          sx={{ background: '#f5f5f5', width: 80, '& input': { padding: '4px 8px' } }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '10px 15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>New Premium</span>
                        <TextField
                          size="small"
                          placeholder="Year"
                          value={newYear}
                          onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setNewYear(e.target.value); }}
                          variant="filled"
                          sx={{ background: '#f5f5f5', width: 80, '& input': { padding: '4px 8px' } }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '10px 15px', width: '60px' }}></td>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client, index) => (
                    <tr key={client.id} style={{ background: '#fff' }}>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc' }}>{index + 1}</td>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Enter client name"
                          value={client.name}
                          onChange={(e) => updateClient(client.id, 'name', e.target.value)}
                          variant="filled"
                          sx={{ background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Enter type of cover"
                          value={client.typeOfCover}
                          onChange={(e) => updateClient(client.id, 'typeOfCover', e.target.value)}
                          variant="filled"
                          sx={{ background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="0.00"
                          type="text"
                          value={client.oldPremium}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*\.?\d*$/.test(val)) updateClient(client.id, 'oldPremium', val);
                          }}
                          variant="filled"
                          sx={{ background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="0.00"
                          type="text"
                          value={client.newPremium}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*\.?\d*$/.test(val)) updateClient(client.id, 'newPremium', val);
                          }}
                          variant="filled"
                          sx={{ background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '8px 15px', borderBottom: '1px solid #ccc', textAlign: 'center' }}>
                        {clients.length > 1 && (
                          <IconButton size="small" onClick={() => removeClient(client.id)} sx={{ color: '#9e9e9e' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {/* Copy Buttons */}
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
              <CopyExcelDataButton
                elementId={'table-premium-increase'}
                style={{ marginRight: 7 }}
              />
              <CopyImageButton
                elementId={'table-premium-increase'}
                setShowImageCopy={setShowImageCopy}
                adjustWidth={false}
              />
            </div>

            {/* Output Table */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <table id="table-premium-increase" className="table-premiums-summary" style={{ width: '100%', tableLayout: 'fixed', background: '#fff', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ backgroundColor: '#050A30', color: '#ebe6e6' }}>
                    <td colSpan={5} style={{ paddingLeft: 20, paddingTop: 15, paddingBottom: 15, textAlign: 'center', fontSize: 16, color: '#ebe6e6' }}>
                      Premium Increase Calculation Table
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#050A30', color: '#ebe6e6', textAlign: 'center' }}>
                    <td style={{ padding: '10px 15px' }}>Client Data</td>
                    <td style={{ padding: '10px 15px' }}>Type of Cover</td>
                    <td style={{ padding: '10px 15px' }}>{oldYear ? `${oldYear} Premium` : 'Old Premium'}</td>
                    <td style={{ padding: '10px 15px' }}>{newYear ? `${newYear} Premium` : 'New Premium'}</td>
                    <td style={{ padding: '10px 15px' }}>% Increase</td>
                  </tr>
                  {clients.map((client) => (
                    <tr key={client.id} style={{ background: '#EDEDED', color: '#050A30', textAlign: 'center' }}>
                      <td style={{ padding: '10px 15px' }}>{client.name || '-'}</td>
                      <td style={{ padding: '10px 15px' }}>{client.typeOfCover || '-'}</td>
                      <td style={{ padding: '10px 15px', textAlign: 'right' }}>{formatNumber(client.oldPremium)}</td>
                      <td style={{ padding: '10px 15px', textAlign: 'right' }}>{formatNumber(client.newPremium)}</td>
                      <td style={{ padding: '10px 15px', color: '#2e7d32' }}>{calculateIncrease(client.oldPremium, client.newPremium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Container>
        </Main>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showImageCopy}
        autoHideDuration={1000}
        onClose={() => setShowImageCopy(false)}
        message="Image Copied to Clipboard"
      />
    </Fragment>
  );
}

export default PremiumIncreaseCalculator;
